import { AppSubStore, AppSubStoreArgs } from "@/store/types";
import { action, makeObservable, observable } from "mobx";
import {
  AudioRecordingUploadingState,
  IAudioUploadingStore,
} from "@/store/audio/uploading/audio-uploading.types";
import { AudioRecordingUploader } from "@/store/audio/uploading/audio-recording-uploader";
import { Uuid } from "@/domains/global/identifiers";

type UploadingStoreConfig = {
  onUploadComplete?: (job: { audioRecordingId: Uuid }) => void;
};

export class AppStoreAudioUploadingStore extends AppSubStore implements IAudioUploadingStore {
  private uploader: AudioRecordingUploader;
  private uploadingStateByRecording = new Map<Uuid, AudioRecordingUploadingState>();

  constructor(
    injectedDeps: AppSubStoreArgs & {
      config?: UploadingStoreConfig;
    }
  ) {
    super(injectedDeps);

    this.uploader = new AudioRecordingUploader(
      {
        audioRecordings: injectedDeps.store.dexie.repositories.audioRecordings,
        blobs: injectedDeps.store.dexie.repositories.localAudioRecordingBlobs,
        transcripts: injectedDeps.store.dexie.repositories.localAudioRecordingTranscriptTurns,
        progressTracker: this,
        modals: injectedDeps.store.modals,
        navigation: injectedDeps.store.navigation,
      },
      {
        onUploadComplete: injectedDeps.config?.onUploadComplete,
      }
    );

    makeObservable<AppStoreAudioUploadingStore, "uploader" | "uploadingStateByRecording">(this, {
      uploader: observable,
      uploadingStateByRecording: observable.shallow,
      enqueue: action,
      setProgress: action,
      clearProgress: action,
      resetState: action,
      getProgress: false,
    });
  }

  async enqueue({ audioRecordingId }: { audioRecordingId: Uuid }): Promise<void> {
    this.uploader.enqueue({
      audioRecordingId,
    });
  }

  resetState() {
    this.uploadingStateByRecording.clear();
    this.uploader.resetState();
  }

  setProgress({
    audioRecordingId,
    state,
  }: {
    audioRecordingId: Uuid;
    state: AudioRecordingUploadingState;
  }): void {
    this.uploadingStateByRecording.set(audioRecordingId, state);
  }

  clearProgress({ audioRecordingId }: { audioRecordingId: Uuid }): void {
    this.uploadingStateByRecording.delete(audioRecordingId);
  }

  getProgress({ audioRecordingId }: { audioRecordingId: Uuid }): AudioRecordingUploadingState {
    return (
      this.uploadingStateByRecording.get(audioRecordingId) ?? {
        uploadProgressPercent: null,
        isUploading: false,
        errorMessage: null,
      }
    );
  }
}
